/**
 * Index-spot levels of an underlying-based (UL) leg, for the positions views.
 *
 * A UL SL / target / W&T is set in index points or % — "UL PTS 10" alone
 * doesn't tell the user WHERE it fires. The backend stamps the actual spot
 * levels on the position row (entry spot, SL / target levels, W&T reference
 * and trigger, exit spot); these helpers turn them into labeled rows, each
 * with how the level was derived ("set at entry spot 22722.25 − 10 pts").
 */
import type { PositionRow } from '../types';
import {
  EXIT_TYPE,
  POSITION_STATUS,
  RISK_MANAGEMENT_TYPE,
  TARGET_PROFIT_TYPE,
  UL_RISK_TYPES,
  UL_TARGET_TYPES,
  UL_WAT_TYPES,
} from '../constants/tradingConstants';
import { getTodayIST } from './dateUtils';

export interface SpotRow {
  label: string;
  value: number;
  /** How the level was derived, e.g. "set at entry spot 22722.25 − 10 pts". */
  note?: string | null;
}

const isSpot = (v: number | null | undefined): v is number => v != null && v > 0;

const offset = (value: number, isPct: boolean) => `${value}${isPct ? '%' : ' pts'}`;

/**
 * UL SL state behind the spot SL row and its note, and the Move SL to Cost
 * display.
 * The UL SL sits on the loss side of the ENTRY spot (backend
 * get_ul_sl_level); the side comes from the backend-computed initial level
 * (pre-TSL-ratchet) instead of a re-derived truth table.
 */
export function ulSlState(p: PositionRow) {
  const isUlSl = !!p.slConfig && UL_RISK_TYPES.includes(p.slConfig.type);
  const entrySpot = p.underlyingEntryPrice;
  const initialSl = p.ulTslInitialSlLevel || p.underlyingSlLevel;
  const trailed = !!p.ulTslInitialSlLevel && p.ulTslInitialSlLevel !== p.underlyingSlLevel;
  const above = initialSl != null && entrySpot != null && initialSl > entrySpot;
  // Mirrors backend should_skip_overnight_sl: a live (OPEN / EXIT_PENDING)
  // row entered before today with the SL "Overnight" box unticked isn't
  // watched today. Closed rows never get this label.
  const offToday = isUlSl
    && (p.status === POSITION_STATUS.OPEN || p.status === POSITION_STATUS.EXIT_PENDING)
    && p.slConfig?.reinstate_overnight === false
    && ((p.entryFilledAt || '').slice(0, 10) || p.executionDate || '') < getTodayIST();
  // Move SL to Cost is tighten-only: a level a UL TSL already moved onto or
  // past the entry spot is left alone.
  const pastCost = isSpot(entrySpot) && isSpot(initialSl) && p.underlyingSlLevel != null
    && (above ? p.underlyingSlLevel <= entrySpot : p.underlyingSlLevel >= entrySpot);
  // Move SL to Cost puts the level exactly on the entry spot.
  const movedToCost = !!p.slMovedToCost && isSpot(entrySpot) && p.underlyingSlLevel === entrySpot;
  return { isUlSl, entrySpot, initialSl, trailed, above, offToday, pastCost, movedToCost };
}

// The spot SL note, and whether it spells out the configured offset.
function slNote(p: PositionRow, s: ReturnType<typeof ulSlState>): { text: string; hasOffset: boolean } | null {
  const cfg = p.slConfig;
  if (!s.isUlSl || !cfg || !isSpot(s.entrySpot) || !isSpot(s.initialSl)) return null;
  if (s.offToday) return { text: 'not watched today — Overnight unticked', hasOffset: false };
  if (s.movedToCost) return { text: `moved to cost — entry spot ${s.entrySpot.toFixed(2)}`, hasOffset: false };
  return {
    text: `${s.trailed ? 'trailed from' : 'set at'} entry spot ${s.entrySpot.toFixed(2)} `
      + `${s.above ? '+' : '−'} ${offset(cfg.value, cfg.type === RISK_MANAGEMENT_TYPE.UL_PERCENTAGE)}`,
    hasOffset: true,
  };
}

/** How the current spot SL level was set (null when it can't be derived). */
export const ulSlNote = (p: PositionRow, s = ulSlState(p)): string | null => slNote(p, s)?.text ?? null;

/**
 * Whether that note spells out the configured offset ("set at entry spot X −
 * 10 pts"), so a "UL PTS 10" hint beside it would only repeat it.
 */
export const ulSlNoteHasOffset = (p: PositionRow, s = ulSlState(p)): boolean => !!slNote(p, s)?.hasOffset;

/** How the spot target level was set (null when it can't be derived). */
export function ulTargetNote(p: PositionRow): string | null {
  const cfg = p.targetProfitConfig;
  const entrySpot = p.underlyingEntryPrice;
  const level = p.underlyingTargetLevel;
  if (!cfg || !UL_TARGET_TYPES.includes(cfg.type) || !isSpot(entrySpot) || !isSpot(level)) return null;
  return `set at entry spot ${entrySpot.toFixed(2)} ${level > entrySpot ? '+' : '−'} `
    + offset(cfg.value, cfg.type === TARGET_PROFIT_TYPE.UL_PERCENTAGE);
}

/**
 * UL_SL / UL_TP when the leg closed on a UL exit, or has claimed one and is
 * still EXIT_PENDING (exit_type is set only on close); else null. Terminating a
 * claimed row leaves pending_exit_type behind, hence the status check.
 */
export function ulExitType(p: PositionRow): string | null {
  const t = p.exitType || (p.status === POSITION_STATUS.EXIT_PENDING ? p.pendingExitType : null);
  return t === EXIT_TYPE.UL_SL || t === EXIT_TYPE.UL_TP ? t : null;
}

/**
 * Spot rows per positions-table cell. A row appears only once its level is
 * stamped (e.g. the SL / target levels land on the first monitor tick after
 * entry).
 *
 * The exit spot is shown only for a UL SL / target exit: the backend stamps it
 * on the exit CLAIM and never clears it when a rejected exit reverts to OPEN,
 * so any other exit (or an open row) would show a stale spot.
 */
export function ulSpotRows(p: PositionRow, slState = ulSlState(p)): {
  entry: SpotRow[]; sl: SpotRow[]; target: SpotRow[]; exit: SpotRow[];
} {
  const ulWat = !!p.waitAndTradeConfig && UL_WAT_TYPES.includes(p.waitAndTradeConfig.type);
  // Labels match describeWatConfig's UL W&T items ('Reference' / 'Spot trigger').
  const entry: SpotRow[] = [];
  if (ulWat && isSpot(p.underlyingReferencePrice)) {
    entry.push({ label: 'Reference', value: p.underlyingReferencePrice });
  }
  if (ulWat && isSpot(p.underlyingEntryTriggerPrice)) {
    entry.push({ label: 'Spot trigger', value: p.underlyingEntryTriggerPrice });
  }
  if (isSpot(p.underlyingEntryPrice)) entry.push({ label: 'Entry spot', value: p.underlyingEntryPrice });

  const sl: SpotRow[] = slState.isUlSl && isSpot(p.underlyingSlLevel)
    ? [{ label: 'Spot SL', value: p.underlyingSlLevel, note: ulSlNote(p, slState) }]
    : [];

  const tp = p.targetProfitConfig;
  const target: SpotRow[] = tp?.enabled && UL_TARGET_TYPES.includes(tp.type) && isSpot(p.underlyingTargetLevel)
    ? [{ label: 'Spot target', value: p.underlyingTargetLevel, note: ulTargetNote(p) }]
    : [];

  const exit: SpotRow[] = ulExitType(p) && isSpot(p.underlyingExitPrice)
    ? [{ label: 'Exit spot', value: p.underlyingExitPrice }]
    : [];

  return { entry, sl, target, exit };
}
